import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Globe, Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import logo from '../../assets/logo.png';

// Carbon-inspired navbar with brand colors and pharmaceutical professional feel
const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const { isAuthenticated, hasRole } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  const toggleMemberDropdown = () => {
    setMemberDropdownOpen(!memberDropdownOpen);
  };



  const getDashboardPath = () => {
    // Check localStorage directly to ensure we always get the correct path
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const roleId = userData.role_id;

        if (roleId === 1) return '/admin/dashboard';
        if (roleId === 2) return '/training-center/dashboard';
        if (roleId === 3) return '/dashboard';
        if (roleId === 4) return '/consultant/dashboard';
      }
    } catch (e) {
      console.error('Failed to parse user data:', e);
    }


    return '/dashboard';
  };

  // Check authentication status from localStorage
  const checkIsAuthenticated = () => {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');
    return !!(token && userStr);
  };

  const isAuthenticatedFromStorage = checkIsAuthenticated();

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${scrolled
        ? 'bg-white shadow-md'
        : 'bg-white shadow-sm'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group" onClick={closeMobileMenu}>
              <img src={logo} alt="SAPI" className="h-8 w-auto" />

            </Link>
          </div>

          {/* Center Navigation - Desktop */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Accueil
            </Link>
            <Link
              to="/Presentation"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Présentation
            </Link>
            <Link
              to="/Actualite"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Actualités
            </Link>
            <Link
              to="/Evenement"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Evenements
            </Link>
            <Link
              to="/Publication"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Publications
            </Link>
            <Link
              to="/Galerie"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Galerie
            </Link>
            <Link
              to="/Contact"
              className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Contact
            </Link>

            {/* Espace membre Dropdown */}
            <div className="relative">
              <button
                onClick={toggleMemberDropdown}
                className="px-4 py-2 text-sm font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors flex items-center space-x-1"
              >
                <User className="w-4 h-4" />
                <span>Espace membre</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${memberDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {memberDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100">
                  {isAuthenticated && hasRole('adherent') ? (
                    <Link
                      to="/adherent/dashboard"
                      onClick={() => setMemberDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                    >
                      Mon espace
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/adherent-login"
                        onClick={() => setMemberDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                      >
                        Se connecter
                      </Link>
                      <Link
                        to="/adhesion"
                        onClick={() => setMemberDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                      >
                        Adhésions
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>





          </div>


          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-200 ease-in-out overflow-hidden ${mobileMenuOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
            }`}
        >
          <div className="border-t border-black/5 pt-4 space-y-1">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Accueil
            </Link>
            <Link
              to="/Presentation"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Présentation
            </Link>
            <Link
              to="/Actualite"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Actualités
            </Link>
            <Link
              to="/Evenement"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Evenements
            </Link>
            <Link
              to="/Publication"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Publications
            </Link>
            <Link
              to="/Galerie"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Galerie
            </Link>
            <Link
              to="/Contact"
              onClick={closeMobileMenu}
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
            >
              Contact
            </Link>

            {/* Espace membre Mobile Dropdown */}
            <div>
              <button
                onClick={toggleMemberDropdown}
                className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-900 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Espace membre</span>
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${memberDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {memberDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1">
                  {isAuthenticated && hasRole('adherent') ? (
                    <Link
                      to="/adherent/dashboard"
                      onClick={closeMobileMenu}
                      className="block px-4 py-2 text-sm text-gray-700 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                    >
                      Mon espace
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/adherent-login"
                        onClick={closeMobileMenu}
                        className="block px-4 py-2 text-sm text-gray-700 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                      >
                        Se connecter
                      </Link>
                      <Link
                        to="/adhesion"
                        onClick={closeMobileMenu}
                        className="block px-4 py-2 text-sm text-gray-700 hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                      >
                        Adhésions
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>




          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 